import { queryClient } from '@/api/queryClient';
import { getAccessToken, getServiceId } from '@/api/tokens.helper';

// The backend no longer serves files straight from the storage bucket: every
// service returns a *private link* — an absolute `…/api/v1/<service>/…` URL
// behind the same bearer token and X-Service-Id header the rest of the API
// uses. A plain `<img src>` / `fetch()` cannot attach those, so such links must
// be resolved through axios first (see `useAuthorizedFileUrl`).
//
// Anything else — a bundled asset, a `blob:`/`data:` preview of a file the user
// just picked, a third-party URL — is used as-is.
export const isAuthorizedFileLink = (link?: null | string): link is string =>
  !!link && link.includes('/api/v1/');

export const AUTHORIZED_FILE_QUERY_KEY = 'authorized-file';

export const authorizedFileQueryKey = (link: null | string | undefined) => [
  AUTHORIZED_FILE_QUERY_KEY,
  link,
];

/**
 * Drop cached file blobs so the next render refetches them.
 *
 * Private links address a file by *id*, not by storage name, so replacing a
 * vessel photo or an avatar hands back the very same URL — and the blob cached
 * under it (staleTime: Infinity, since a link's content used to be immutable)
 * would keep showing the old image. Call this after any upload that overwrites
 * a file in place; pass the link when it is known (its variants go with it),
 * omit it to clear them all.
 */
export const invalidateAuthorizedFile = (link?: null | string) => {
  if (!link) {
    void queryClient.invalidateQueries({ queryKey: [AUTHORIZED_FILE_QUERY_KEY] });
    return;
  }
  // The image variants (`<link>/variants/<name>`) were regenerated with it.
  const variantsPrefix = `${link.split('?')[0]}/variants/`;
  void queryClient.invalidateQueries({
    queryKey: [AUTHORIZED_FILE_QUERY_KEY],
    predicate: ({ queryKey: [, cached] }) =>
      cached === link || (typeof cached === 'string' && cached.startsWith(variantsPrefix)),
  });
};

// Chat message files and gallery memories answer byte ranges, so a <video> can
// start a clip while it is still arriving and seek past what has arrived — but
// only when the element makes the requests itself, and an element cannot attach
// headers. The shared auth and seat middlewares read both credentials from the
// query string too (`?jwt=`, `?service_id=`), so these links are signed into the
// URL rather than fetched whole as a blob. Every other file endpoint is fetched
// whole.
const STREAMABLE_FILE_PATHS = ['/api/v1/chat/messages/files/', '/api/v1/activity/files/gallery/'];

export const isStreamableFileLink = (link?: null | string): link is string =>
  !!link && STREAMABLE_FILE_PATHS.some((path) => link.includes(path));

/** The link with the current access token and service id in its query string. */
export const signStreamingLink = (link: string) => {
  const url = new URL(link, window.location.origin);
  url.searchParams.set('jwt', getAccessToken());

  const serviceId = getServiceId();
  if (serviceId) url.searchParams.set('service_id', serviceId);

  return url.toString();
};
